/**
 * R268hh · Feb 2026 — Live totals preview panel for the Composer.
 *
 * Displays taxable value, CGST/SGST/UGST or IGST, cess, additional
 * charges, round-off, grand total, and TDS net-receivable when a TDS
 * section is applied. Populated from the debounced /gst/compute call
 * that runs in the parent Composer.
 *
 * Feb 2026 · Currency aware — props.currency drives the symbol so
 * foreign-currency drafts don't display totals as rupees. Exports
 * (currency ≠ home currency) are typically zero-rated under GST, so
 * the GST rows (CGST/SGST/IGST/cess + state hint) collapse away.
 *
 * Pure display — safe to memoise. Parent only re-renders this when
 * `preview`, `tdsRate`, `currency` or `homeCurrency` actually change.
 */
import { memo } from "react";
import { PreviewRow } from "./shared";
import { fmtMoney } from "@/lib/currency";

function TotalsPanel({ preview, tdsRate, currency = "INR", homeCurrency = "INR", fxRate = 1 }) {
  if (!preview) return null;
  const t = preview.totals;
  const isForeign = currency && currency !== homeCurrency;
  // Exports → zero-rated under GST. Hide tax rows so the preview matches
  // what the customer will see on the PDF. Keep taxable, charges, round
  // off, grand total + TDS.
  const showGst = !isForeign;
  return (
    <div className="rounded-xl border border-border p-3 bg-muted/30 space-y-1 text-sm"
            data-testid="gst-composer-preview">
      {isForeign && (
        <div className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-300 mb-1"
             data-testid="gst-composer-preview-foreign-badge">
          Export · zero-rated under GST · amounts in {currency}
        </div>
      )}
      <PreviewRow label="Taxable value" value={t.total_taxable} currency={currency} />
      {showGst && (t.is_intra_state ? (
        <>
          <PreviewRow label="CGST" value={t.cgst_total} currency={currency} />
          <PreviewRow
            label={t.is_ut ? "UGST" : "SGST"}
            value={t.is_ut ? t.ugst_total : t.sgst_total}
            currency={currency}
          />
        </>
      ) : (
        <PreviewRow label="IGST" value={t.igst_total} currency={currency} />
      ))}
      {showGst && t.cess_total > 0 &&
        <PreviewRow label="Cess" value={t.cess_total} currency={currency} />}
      {t.additional_charges > 0 &&
        <PreviewRow label="Additional charges" value={t.additional_charges} currency={currency} />}
      {t.round_off !== 0 &&
        <PreviewRow label="Round off" value={t.round_off} currency={currency} />}
      <div className="border-t border-border my-1" />
      <PreviewRow bold label="Grand total" value={t.grand_total} currency={currency} />
      {isForeign && Number(fxRate) > 0 && (
        <div className="flex justify-between text-[11px] text-muted-foreground tabular-nums"
             data-testid="gst-composer-preview-home-equiv">
          <span>≈ in {homeCurrency}</span>
          <span>{fmtMoney(Number(t.grand_total) * Number(fxRate), homeCurrency)}</span>
        </div>
      )}
      {t.tds_amount > 0 && (
        <>
          <PreviewRow label={`Less: TDS (${tdsRate}%)`} value={-t.tds_amount} currency={currency} />
          <PreviewRow bold label="Net receivable" value={t.net_receivable} currency={currency} />
        </>
      )}
      <div className="pt-1 text-[11px] text-muted-foreground">
        {showGst
          ? (t.is_intra_state
              ? `Intra-state · ${t.supplier_state_code}`
              : `Inter-state · ${t.supplier_state_code} → ${t.place_of_supply_code}`)
          : `Export / foreign currency · GST not applicable`}
      </div>
    </div>
  );
}

export default memo(TotalsPanel);
